import { AnchorOrientation } from "../common";
import { BehaviorData } from "../behaviordata";

/** Type identifier for ImageAnchor behavior. */
export const IMAGE_ANCHOR_TYPE = "ImageAnchor" as const;

/**
 * Name of the marker preview mesh created by the {@link ImageAnchor}
 * behavior. Owned here (not in the behavior class) so other layers
 * (e.g. `SceneCamera.getSceneBounds`) can reference it without forming
 * a `core → behaviors` import cycle.
 */
export const ANCHOR_PREVIEW_MESH_NAME = "__anchor_preview__";

/**
 * Describes an image-marker anchor for a scene.
 *
 * Attached to the scene root in editor mode as a UI affordance: it renders a
 * preview of the printed marker (textured plane at physical scale) and lets
 * the author edit the marker's expected orientation. Source-of-truth for the
 * marker URL and physical width currently lives on the CMS Scene record;
 * this behavior is CMS-managed and is excluded from `.trace` export. It
 * round-trips through the publish-metadata channel.
 */
export interface ImageAnchorData extends BehaviorData {
    "@type": typeof IMAGE_ANCHOR_TYPE;

    /**
     * URL of the marker image asset.
     */
    src?: string;

    /**
     * Physical width of the printed marker in meters.
     * @default 0.2
     * @step 0.01
     */
    widthMeters?: number;

    /**
     * Expected marker orientation in the world.
     * `Free` means the runtime accepts any detected pose.
     * @default "Free"
     */
    orientation?: AnchorOrientation;

    /**
     * Pitch of the marker in degrees, about the marker's local X axis
     * (image-right). Positive values tilt the marker's top edge away
     * from the viewer.
     *
     * `orientation` and `pitchDegrees` describe the same pose at
     * different fidelities — `pitchDegrees` is the full angle, while
     * `orientation` is a semantic label (Wall / Floor / Free). When
     * `orientation` is `Vertical` or `Horizontal` it gives the complete
     * pose and `pitchDegrees` is ignored. When `orientation` is `Free`
     * or unset, `pitchDegrees` provides the absolute angle.
     *
     * @default 0
     * @step 1
     */
    pitchDegrees?: number;
}
